import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { C } from './constants';
import { getPeriodeInfo, syncAnnuaire, postToWallOfFame, DecouverteTour } from './App';
import { EquipePanel, resultatEquipe, chargerAnnuaire, EQUIPE_VIDE, FeuilleModale } from './EquipeGains';
import { remiseCommande } from './PlanMarketing';

// ─────────────────────────────────────────────────────────────────────────────
// 🐷 TIRELIRE : l'objectif n'est plus un nombre de points mais de l'ARGENT à aller chercher.
//  - la distributrice fixe combien elle veut gagner (€) et son pourcentage (20 à 30 %)
//  - elle saisit ses ventes réelles (prix de vente) : gain = ventes × pourcentage
//  - la tirelire se remplit, et un bandeau reste visible sur tous les onglets
// Données : champ "db-tirelire" du document users/{uid} (JSON), remis à zéro à chaque période
//   { periode, gainObj, taux, ventes:[{id,date,montant,note}], historique:[{periode,gainObj,taux,ventes,gain}] }
// ─────────────────────────────────────────────────────────────────────────────

const TAUX_MIN = 20;
const TAUX_MAX = 30;
const TAUX_DEFAUT = 25;
const OBJECTIFS_RAPIDES = [50, 100, 200, 300, 500, 1000, 2000, 5000];

const num = (v) => {
  const n = parseFloat(String(v == null ? "" : v).replace(/\s/g, "").replace(",", "."));
  return isNaN(n) || n < 0 ? 0 : n;
};
const arrondi = (n) => Math.round(n * 100) / 100;
export const fmtEuro = (n) => {
  const v = arrondi(n);
  return v.toLocaleString("fr-FR", Number.isInteger(v) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};
const aujourdhui = () => {
  const d = new Date();
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};
const dateCourte = (iso) => {
  const d = new Date((iso || "") + "T12:00:00");
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};
const periodeCourante = () => {
  try { const p = getPeriodeInfo(); return p && p.periodNum ? p.periodNum : 0; } catch { return 0; }
};

// Calculs partagés par le bandeau et la fenêtre
export function calculerTirelire(t) {
  const taux = Math.min(TAUX_MAX, Math.max(TAUX_MIN, num(t && t.taux) || TAUX_DEFAUT));
  const ventes = (t && Array.isArray(t.ventes)) ? t.ventes : [];
  const totalVentes = ventes.reduce((s, v) => s + num(v.montant), 0);
  const gainVentes = arrondi(totalVentes * taux / 100);
  const gainEquipe = arrondi(num(t && t.equipeGain));          // calculé par le panneau « Mon équipe » (plan marketing)
  const gain = arrondi(gainVentes + gainEquipe);
  const gainObj = num(t && t.gainObj);
  const pct = gainObj > 0 ? gain / gainObj : 0;
  const ventesNecessaires = gainObj > 0 ? Math.max(0, gainObj - gainEquipe) / (taux / 100) : 0;
  const ventesRestantes = Math.max(0, arrondi(ventesNecessaires - totalVentes));
  return { taux, ventes, totalVentes: arrondi(totalVentes), gainVentes, gainEquipe, gain, gainObj, pct, ventesNecessaires: arrondi(ventesNecessaires), ventesRestantes };
}

function message(pct, aObjectif) {
  if (!aObjectif) return "Dis-moi combien tu veux aller chercher, et on remplit ta tirelire ensemble.";
  if (pct >= 1) return "Tirelire pleine, objectif atteint ! 🎉 Tu peux viser plus haut.";
  if (pct >= 0.75) return "Presque pleine, encore un petit effort ! ✨";
  if (pct >= 0.5) return "Plus de la moitié, tu es sur la bonne voie 🔥";
  if (pct >= 0.25) return "Elle se remplit, continue comme ça 💪";
  if (pct > 0) return "Les premières pièces sont tombées, bravo ! 🪙";
  return "Ta tirelire attend sa première vente 🐷";
}

const CSS = `
@keyframes tirCoin{0%{transform:translateY(-34px);opacity:0}15%{opacity:1}75%{transform:translateY(18px);opacity:1}100%{transform:translateY(24px);opacity:0}}
@keyframes tirWave{0%{transform:translateX(0)}100%{transform:translateX(-100px)}}
@keyframes tirPop{0%{transform:scale(1)}40%{transform:scale(1.06)}100%{transform:scale(1)}}
@keyframes tirConf{0%{transform:translateY(0) rotate(0);opacity:1}100%{transform:translateY(-120px) rotate(300deg);opacity:0}}
@keyframes tirGain{0%{transform:translateY(8px);opacity:0}30%{opacity:1}100%{transform:translateY(-26px);opacity:0}}
`;

function BoutonDecouverte({ onClick }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: ".5rem" }}>
      <button onClick={onClick} style={{ background: "#C49A8A", color: "white", border: "none", borderRadius: 20, padding: ".35rem 1rem", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", boxShadow: "0 2px 8px rgba(196,154,138,.4)" }}>🧭 Découverte</button>
    </div>
  );
}

// ── Le cochon-tirelire (SVG) qui se remplit de bas en haut ───────────────────
function Cochon({ pct, taille = 120, idp = "t", piece = 0 }) {
  const p = Math.max(0, Math.min(1, pct || 0));
  const haut = 128 - 100 * p;       // le corps va de y=28 (haut) à y=128 (bas)
  return (
    <svg viewBox="0 0 200 150" width={taille} height={taille * 0.75} style={{ display: "block", overflow: "visible" }} aria-hidden="true">
      <defs>
        <clipPath id={idp + "-corps"}><ellipse cx="100" cy="78" rx="72" ry="50" /></clipPath>
        <linearGradient id={idp + "-or"} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#F8DE8A" /><stop offset="1" stopColor="#E0A93B" />
        </linearGradient>
      </defs>
      {/* queue + pattes (derrière le corps) */}
      <path d="M30 80 q-16 -2 -12 -16 q4 -9 -5 -12" fill="none" stroke={C.rose} strokeWidth="5" strokeLinecap="round" />
      <rect x="52" y="112" width="24" height="26" rx="9" fill={C.rose} />
      <rect x="126" y="112" width="24" height="26" rx="9" fill={C.rose} />
      {/* corps */}
      <ellipse cx="100" cy="78" rx="72" ry="50" fill="#FFF3F0" />
      <g clipPath={`url(#${idp}-corps)`}>
        <g style={{ transform: `translateY(${haut}px)`, transition: "transform .9s cubic-bezier(.3,.9,.3,1)" }}>
          <g style={{ animation: "tirWave 3.2s linear infinite" }}>
            <path d="M0 6 Q25 -2 50 6 T100 6 T150 6 T200 6 T250 6 T300 6 T350 6 T400 6 V160 H0 Z" fill={`url(#${idp}-or)`} />
          </g>
        </g>
        {/* petites pièces dans la tirelire */}
        {p > 0.12 && <circle cx="78" cy="112" r="7" fill="#F6D060" stroke="#C99A2E" strokeWidth="1.5" />}
        {p > 0.3 && <circle cx="104" cy="108" r="7" fill="#F6D060" stroke="#C99A2E" strokeWidth="1.5" />}
        {p > 0.55 && <circle cx="128" cy="100" r="7" fill="#F6D060" stroke="#C99A2E" strokeWidth="1.5" />}
      </g>
      {/* reflet */}
      <ellipse cx="68" cy="52" rx="16" ry="7" fill="#fff" opacity=".45" transform="rotate(-22 68 52)" />
      <ellipse cx="100" cy="78" rx="72" ry="50" fill="none" stroke={C.brun} strokeWidth="3" />
      {/* oreille, groin, oeil */}
      <path d="M134 36 L150 12 L164 44 Z" fill={C.rose} stroke={C.brun} strokeWidth="2.5" strokeLinejoin="round" />
      <ellipse cx="172" cy="84" rx="15" ry="12" fill="#F2B8AE" stroke={C.brun} strokeWidth="2.5" />
      <circle cx="168" cy="84" r="2.3" fill={C.brun} /><circle cx="177" cy="84" r="2.3" fill={C.brun} />
      <circle cx="146" cy="66" r="4.5" fill={C.brun} />
      <circle cx="140" cy="86" r="8" fill="#F2B8AE" opacity=".55" />
      {/* fente de la tirelire */}
      <rect x="82" y="24" width="38" height="8" rx="4" fill={C.brun} />
      {/* pièce qui tombe */}
      {piece > 0 && (
        <g key={piece} style={{ animation: "tirCoin .9s ease-in forwards" }}>
          <circle cx="101" cy="14" r="12" fill="#F6D060" stroke="#C99A2E" strokeWidth="2" />
          <text x="101" y="19" textAnchor="middle" fontSize="14" fontWeight="700" fill="#8B6A12">€</text>
        </g>
      )}
    </svg>
  );
}

function Confettis() {
  const pieces = ["🪙", "✨", "💰", "🎉", "💖", "🪙", "✨", "🎊"];
  return (
    <div style={{ position: "absolute", left: 0, right: 0, top: 80, height: 0, pointerEvents: "none", zIndex: 5 }}>
      {pieces.map((e, i) => (
        <span key={i} style={{ position: "absolute", left: (8 + i * 12) + "%", fontSize: "1.3rem", animation: `tirConf ${1.4 + (i % 3) * 0.3}s ease-out ${i * 0.07}s forwards` }}>{e}</span>
      ))}
    </div>
  );
}

// ── Le bandeau toujours visible + la fenêtre de la tirelire ──────────────────
function TirelireMoitie({ uid, userName }) {
  const [t, setT] = useState(null);          // null tant que ce n'est pas chargé
  const [ouvert, setOuvert] = useState(false);
  const [objDraft, setObjDraft] = useState("");
  const [montant, setMontant] = useState("");
  const [note, setNote] = useState("");
  const [piece, setPiece] = useState(0);
  const [gainAjoute, setGainAjoute] = useState(null);
  const [fete, setFete] = useState(0);
  const [equipeOuverte, setEquipeOuverte] = useState(false);
  const [tour, setTour] = useState(false);
  const [pvObj, setPvObj] = useState(0);        // « ventes perso » déjà saisies dans Mes objectifs
  const [cmdCatalogue, setCmdCatalogue] = useState("");
  const timer = useRef(null);
  const dernier = useRef(null);

  // Enregistrement (légèrement retardé pour ne pas écrire à chaque mouvement du curseur)
  const enregistrer = useCallback((next) => {
    dernier.current = next;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      timer.current = null;
      try { await setDoc(doc(db, "users", uid), { "db-tirelire": JSON.stringify(dernier.current) }, { merge: true }); } catch (e) { console.warn("tirelire:", e); }
    }, 500);
  }, [uid]);

  const maj = useCallback((fn) => {
    setT((cur) => {
      const next = fn(cur || { periode: periodeCourante(), gainObj: "", taux: TAUX_DEFAUT, ventes: [], historique: [] });
      if (next !== cur) enregistrer(next);
      return next;
    });
  }, [enregistrer]);

  // Chargement + passage automatique à la période suivante
  useEffect(() => {
    if (!uid) return;
    let vivant = true;
    (async () => {
      let data = null;
      let pvDepuisObjectifs = 0;
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists() && snap.data()["db-tirelire"]) data = JSON.parse(snap.data()["db-tirelire"]);
        if (snap.exists() && snap.data()["db-obj-perso"]) {
          try { pvDepuisObjectifs = num(JSON.parse(snap.data()["db-obj-perso"]).caPerso); } catch { /* ignore */ }
        }
      } catch { /* hors ligne : on repart d'une tirelire vide */ }
      const p = periodeCourante();
      let base = data && typeof data === "object" ? data : { periode: p, gainObj: "", taux: TAUX_DEFAUT, ventes: [], historique: [] };
      base = { gainObj: "", taux: TAUX_DEFAUT, ventes: [], historique: [], ...base, periode: base.periode || p };
      if (base.periode !== p) {
        // Nouvelle période : on archive la précédente et on repart de zéro (objectif et % conservés)
        const c = calculerTirelire(base);
        const histo = c.totalVentes > 0 ? [...(base.historique || []), { periode: base.periode, gainObj: c.gainObj, taux: c.taux, ventes: c.totalVentes, gain: c.gain }].slice(-12) : (base.historique || []);
        // L'équipe garde ses noms, mais les volumes tapés à la main sont effacés (nouvelle période)
        const eq = base.equipe ? { ...base.equipe, pv: "", membres: (base.equipe.membres || []).map((m) => ({ ...m, dernierPgv: (m.pgvManuel != null && String(m.pgvManuel).trim() !== "") ? m.pgvManuel : (m.dernierPgv || ""), pgvManuel: "", pvManuel: "" })) } : base.equipe;
        base = { ...base, periode: p, ventes: [], historique: histo, equipe: eq, equipeGain: 0 };
        enregistrer(base);
      }
      if (vivant) { setT(base); setPvObj(pvDepuisObjectifs); setObjDraft(base.gainObj ? String(base.gainObj) : ""); }
      // Le chiffre des personnes de l'équipe remonte seul : on met à jour le gain d'équipe en arrière-plan
      if (base.equipe && (base.equipe.membres || []).some((m) => m.uid)) {
        const annuaire = await chargerAnnuaire(uid);
        if (annuaire && vivant) {
          const ventesTotal = (base.ventes || []).reduce((s, v) => s + num(v.montant), 0);
          const g = resultatEquipe(base.equipe, annuaire, pvDepuisObjectifs > 0 ? pvDepuisObjectifs : ventesTotal).total;
          setT((cur) => {
            if (!cur || arrondi(num(cur.equipeGain)) === g) return cur;
            const next = { ...cur, equipeGain: g };
            enregistrer(next);
            return next;
          });
        }
      }
    })();
    return () => { vivant = false; };
  }, [uid, enregistrer]);

  // Permet d'ouvrir la tirelire depuis ailleurs : window.dispatchEvent(new Event("ouvrir-tirelire"))
  useEffect(() => {
    const ouvrir = () => setOuvert(true);
    window.addEventListener("ouvrir-tirelire", ouvrir);
    return () => window.removeEventListener("ouvrir-tirelire", ouvrir);
  }, []);

  // Sécurité : on enregistre ce qui est en attente si la page est quittée
  useEffect(() => () => {
    if (timer.current && dernier.current) {
      clearTimeout(timer.current);
      try { setDoc(doc(db, "users", uid), { "db-tirelire": JSON.stringify(dernier.current) }, { merge: true }); } catch { /* ignore */ }
    }
  }, [uid]);

  // Chaque vente ajoutée / retirée ici est aussi ajoutée / retirée dans « ventes perso » de Mes objectifs
  // (on ajoute seulement la différence : ce que tu as tapé toi-même dans Objectifs n'est jamais écrasé)
  const chaineObj = useRef(Promise.resolve());
  const refleterVentes = useCallback((delta) => {
    if (!delta) return;
    chaineObj.current = chaineObj.current.then(async () => {
      try {
        const ref = doc(db, "users", uid);
        const snap = await getDoc(ref);
        const o = snap.exists() && snap.data()["db-obj-perso"] ? JSON.parse(snap.data()["db-obj-perso"]) : {};
        const caPerso = Math.max(0, arrondi(num(o.caPerso) + delta));
        const ca = Math.max(0, arrondi(num(o.ca) + delta));
        const next = { ...o, caPerso: String(caPerso), ca: String(ca), caEquipe: String(Math.max(0, arrondi(ca - caPerso))) };
        await setDoc(ref, { "db-obj-perso": JSON.stringify(next) }, { merge: true });
        setPvObj(caPerso);
        try { await syncAnnuaire(uid, userName || uid, next); } catch { /* ignore */ }
        try { window.dispatchEvent(new CustomEvent("ventes-maj", { detail: { ca: next.ca, caPerso: next.caPerso, caEquipe: next.caEquipe } })); } catch { /* ignore */ }
      } catch (e) { console.warn("ventes → objectifs:", e); }
    });
  }, [uid, userName]);

  // Le panneau « Mon équipe » nous donne le gain calculé : on le garde dans la tirelire
  const onGainEquipe = useCallback((g) => {
    maj((cur) => (arrondi(num(cur.equipeGain)) === g ? cur : { ...cur, equipeGain: g }));
  }, [maj]);

  if (!uid) return null;
  if (!t) return <div style={{ flex: "1 1 0" }} />;

  const c = calculerTirelire(t);
  const aObjectif = c.gainObj > 0;
  const pctAff = Math.min(100, Math.round(c.pct * 100));
  const pleine = aObjectif && c.pct >= 1;
  let joursRestants = null;
  try { joursRestants = getPeriodeInfo().daysLeft; } catch { /* ignore */ }

  const validerObjectif = (valeur) => {
    const v = num(valeur);
    maj((cur) => ({ ...cur, gainObj: v > 0 ? v : "" }));
    setObjDraft(v > 0 ? String(v) : "");
  };

  const ajouterVente = () => {
    const m = num(montant);
    if (m <= 0) return;
    const avant = c.pct;
    const gainVente = arrondi(m * c.taux / 100);
    maj((cur) => ({ ...cur, ventes: [{ id: Date.now(), date: aujourdhui(), montant: arrondi(m), note: note.trim(), lie: true }, ...(cur.ventes || [])] }));
    refleterVentes(arrondi(m));
    setMontant(""); setNote("");
    setPiece((n) => n + 1);
    setGainAjoute({ id: Date.now(), v: gainVente });
    setTimeout(() => setGainAjoute(null), 1600);
    if (aObjectif && avant < 1 && (c.totalVentes + m) * c.taux / 100 + c.gainEquipe >= c.gainObj) setFete((n) => n + 1);
  };

  const retirerVente = (id) => {
    const v = (t.ventes || []).find((x) => x.id === id);
    maj((cur) => ({ ...cur, ventes: (cur.ventes || []).filter((x) => x.id !== id) }));
    // seules les ventes déjà reportées dans Objectifs y sont retirées
    if (v && v.lie) refleterVentes(-arrondi(num(v.montant)));
  };

  const champ = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${C.pale}`, borderRadius: 10, padding: ".6rem .75rem", fontSize: "1rem", fontFamily: "inherit", color: C.brun, background: C.creme, outline: "none", fontWeight: 600 };
  const etiquette = { fontSize: ".62rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: C.rose, marginBottom: ".4rem" };
  const bloc = { background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: 14, padding: ".9rem", marginBottom: ".7rem" };
  const chip = (actif) => ({ padding: ".35rem .75rem", fontSize: ".72rem", fontWeight: 700, borderRadius: 20, cursor: "pointer", fontFamily: "inherit", border: `1.5px solid ${actif ? C.rose : C.pale}`, background: actif ? C.rose : C.blanc, color: actif ? "white" : C.gris });

  return (
    <>
      <style>{CSS}</style>

      {/* ── MOITIÉ « TIRELIRE » DU BANDEAU TOUJOURS VISIBLE ── */}
      <div onClick={() => setOuvert(true)} role="button" aria-label="Ouvrir ma tirelire"
        style={{ flex: "1 1 0", minWidth: 0, padding: ".4rem .5rem .4rem .7rem", display: "flex", alignItems: "center", gap: ".45rem", cursor: "pointer" }}>
        <div style={{ flexShrink: 0, width: 38, animation: pleine ? "tirPop 1.6s ease-in-out infinite" : "none" }}>
          <Cochon pct={c.pct} taille={38} idp="tb" />
        </div>
        {aObjectif ? (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: ".5rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: C.or }}>Ma tirelire</div>
            <div style={{ fontSize: ".72rem", fontWeight: 700, color: "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {fmtEuro(c.gain)} <span style={{ fontWeight: 400, opacity: .7, fontSize: ".6rem" }}>/ {fmtEuro(c.gainObj)}</span>
            </div>
            <div style={{ height: 5, background: "rgba(255,255,255,.18)", borderRadius: 10, overflow: "hidden", marginTop: ".18rem" }}>
              <div style={{ height: "100%", width: Math.max(2, pctAff) + "%", background: pleine ? C.vert : "linear-gradient(90deg,#F6D060,#E0A93B)", borderRadius: 10, transition: "width .8s ease" }} />
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: ".5rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: C.or }}>Ma tirelire</div>
            <div style={{ fontSize: ".66rem", fontWeight: 600, color: "white", lineHeight: 1.3 }}>Combien veux-tu gagner ? 💰</div>
          </div>
        )}
      </div>

      {/* ── FENÊTRE DE LA TIRELIRE ── */}
      {ouvert && (
        <FeuilleModale onClose={() => setOuvert(false)}>
            {fete > 0 && <Confettis key={fete} />}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".4rem" }}>
              <div>
                <div style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem", fontWeight: 400, color: C.brun }}>Ma <em style={{ color: C.rose }}>tirelire</em></div>
                {joursRestants != null && <div style={{ fontSize: ".62rem", color: C.gris }}>Période en cours · {joursRestants} jour{joursRestants > 1 ? "s" : ""} restant{joursRestants > 1 ? "s" : ""}</div>}
              </div>
              <button onClick={() => setOuvert(false)} aria-label="Fermer"
                style={{ background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: "50%", width: 34, height: 34, fontSize: "1rem", color: C.gris, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
            </div>

            <BoutonDecouverte onClick={() => setTour(true)} />
            {tour && <DecouverteTour outil="tirelire" onClose={() => setTour(false)} />}

            {/* Grosse tirelire */}
            <div id="decouverte-tirelire-cochon" style={{ position: "relative", textAlign: "center", padding: ".3rem 0 .2rem" }}>
              <div style={{ display: "inline-block", animation: pleine ? "tirPop 1.6s ease-in-out infinite" : "none" }}>
                <Cochon pct={c.pct} taille={230} idp="tg" piece={piece} />
              </div>
              {gainAjoute && (
                <div key={gainAjoute.id} style={{ position: "absolute", left: 0, right: 0, top: "38%", fontSize: "1.3rem", fontWeight: 800, color: C.vert, animation: "tirGain 1.5s ease-out forwards", pointerEvents: "none", textShadow: "0 1px 4px rgba(255,255,255,.9)" }}>
                  +{fmtEuro(gainAjoute.v)}
                </div>
              )}
              <div style={{ fontFamily: "Georgia,serif", fontSize: "2rem", fontWeight: 600, color: pleine ? C.vert : C.brun, lineHeight: 1.1 }}>{fmtEuro(c.gain)}</div>
              <div style={{ fontSize: ".74rem", color: C.gris }}>
                {aObjectif ? <>gagnés sur <strong style={{ color: C.brun }}>{fmtEuro(c.gainObj)}</strong> visés · {pctAff} %</> : "gagnés pour l'instant"}
              </div>
              {c.gainEquipe > 0 && (
                <div style={{ fontSize: ".66rem", color: C.gris, marginTop: ".15rem" }}>
                  dont <strong style={{ color: C.brun }}>{fmtEuro(c.gainVentes)}</strong> de mes ventes · <strong style={{ color: C.brun }}>{fmtEuro(c.gainEquipe)}</strong> de mon équipe
                </div>
              )}
              <div style={{ fontSize: ".78rem", color: C.brun, marginTop: ".4rem", lineHeight: 1.5, fontStyle: "italic" }}>{message(c.pct, aObjectif)}</div>
            </div>

            {/* 1. Combien */}
            <div id="decouverte-tirelire-objectif" style={{ ...bloc, marginTop: ".8rem" }}>
              <div style={etiquette}>🎯 Combien je vais chercher</div>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center" }}>
                <input type="text" inputMode="decimal" placeholder="Tape ton montant (ex : 150)" value={objDraft}
                  onChange={(e) => setObjDraft(e.target.value)} onBlur={() => validerObjectif(objDraft)}
                  onKeyDown={(e) => { if (e.key === "Enter") { validerObjectif(objDraft); e.currentTarget.blur(); } }}
                  style={champ} />
                <span style={{ fontSize: "1.1rem", fontWeight: 700, color: C.brun }}>€</span>
              </div>
              <div style={{ fontSize: ".62rem", color: C.gris, margin: ".35rem 0 .5rem" }}>Choisis un montant ci-dessous ou tape le tien, même petit : 50 €, 80 €, 120 €... C'est l'argent que tu veux GAGNER pour toi sur cette période, pas un nombre de points.</div>
              <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                {OBJECTIFS_RAPIDES.map((v) => (
                  <button key={v} onClick={() => validerObjectif(v)} style={chip(c.gainObj === v)}>{v} €</button>
                ))}
              </div>
            </div>

            {/* 2. Pourcentage */}
            <div id="decouverte-tirelire-taux" style={bloc}>
              <div style={etiquette}>📊 Mon pourcentage de gain</div>
              <div style={{ display: "flex", alignItems: "center", gap: ".7rem" }}>
                <input type="range" min={TAUX_MIN} max={TAUX_MAX} step="1" value={c.taux}
                  onChange={(e) => maj((cur) => ({ ...cur, taux: +e.target.value }))}
                  style={{ flex: 1, accentColor: C.rose }} aria-label="Pourcentage de gain" />
                <div style={{ fontFamily: "Georgia,serif", fontSize: "1.4rem", fontWeight: 700, color: C.rose, minWidth: 54, textAlign: "right" }}>{c.taux} %</div>
              </div>
              <div style={{ display: "flex", gap: ".4rem", margin: ".5rem 0 .3rem" }}>
                {[20, 25, 30].map((v) => <button key={v} onClick={() => maj((cur) => ({ ...cur, taux: v }))} style={chip(c.taux === v)}>{v} %</button>)}
              </div>
              <div style={{ fontSize: ".64rem", color: C.gris, lineHeight: 1.5 }}>Tu gagnes entre 20 et 30 % sur tes ventes. À {c.taux} %, <strong style={{ color: C.brun }}>100 € vendus = {fmtEuro(c.taux)}</strong> pour toi.</div>
              <div style={{ marginTop: ".6rem", background: C.creme, borderRadius: 10, padding: ".55rem .65rem" }}>
                <div style={{ fontSize: ".62rem", color: C.brun, lineHeight: 1.5, marginBottom: ".35rem" }}>
                  <strong>Comment savoir ?</strong> Ta remise dépend du montant de ta commande (prix catalogue) : <strong>20 %</strong> jusqu'à 70 €, <strong>25 %</strong> au-dessus de 70 €, <strong>30 %</strong> au-dessus de 145 €.
                </div>
                <div style={{ display: "flex", gap: ".4rem", alignItems: "center" }}>
                  <input type="text" inputMode="decimal" value={cmdCatalogue} onChange={(e) => setCmdCatalogue(e.target.value)} placeholder="Ma commande (€)"
                    style={{ ...champ, fontSize: ".8rem", fontWeight: 500, padding: ".4rem .6rem" }} />
                  {num(cmdCatalogue) > 0 && (
                    <button onClick={() => { maj((cur) => ({ ...cur, taux: remiseCommande(num(cmdCatalogue)) })); }}
                      style={{ flexShrink: 0, background: C.rose, color: "white", border: "none", borderRadius: 8, padding: ".45rem .65rem", fontSize: ".68rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>
                      → {remiseCommande(num(cmdCatalogue))} %
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Mon équipe */}
            <div id="decouverte-tirelire-equipe" onClick={() => setEquipeOuverte(true)} role="button"
              style={{ background: "linear-gradient(135deg,#F3EAF8,#E8DDF3)", border: `1.5px solid ${C.lilas || C.or}`, borderRadius: 14, padding: ".85rem .9rem", marginBottom: ".7rem", cursor: "pointer", display: "flex", alignItems: "center", gap: ".7rem" }}>
              <span style={{ fontSize: "1.7rem" }}>👥</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: ".62rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: C.lilas || C.rose }}>Gagner grâce à mon équipe</div>
                <div style={{ fontSize: ".76rem", color: C.brun, lineHeight: 1.45 }}>
                  {c.gainEquipe > 0
                    ? <>Mon équipe me rapporte <strong>{fmtEuro(c.gainEquipe)}</strong> cette période. Touche pour voir le détail.</>
                    : "Ajoute les personnes de ton équipe : leur chiffre remonte seul et on calcule ce que tu gagnes avec elles."}
                </div>
              </div>
              <span style={{ color: C.lilas || C.rose, fontSize: "1.1rem" }}>›</span>
            </div>

            {/* Le calcul magique */}
            {aObjectif && (
              <div style={{ background: "linear-gradient(135deg,#FFF8E1,#FDEBD3)", border: `1px solid ${C.or}66`, borderRadius: 14, padding: ".8rem .9rem", marginBottom: ".7rem" }}>
                <div style={{ fontSize: ".74rem", color: C.brun, lineHeight: 1.6 }}>
                  {c.gainEquipe > 0 && <>Ton équipe te rapporte déjà <strong>{fmtEuro(c.gainEquipe)}</strong>. </>}
                  Pour gagner <strong>{fmtEuro(c.gainObj)}</strong> à <strong>{c.taux} %</strong>, il te faut <strong>{fmtEuro(c.ventesNecessaires)}</strong> de ventes perso.
                </div>
                <div style={{ fontSize: ".82rem", fontWeight: 700, color: pleine ? C.vert : C.rose, marginTop: ".25rem" }}>
                  {pleine ? "🎉 C'est fait, bravo !" : `Il te reste ${fmtEuro(c.ventesRestantes)} de ventes à faire.`}
                </div>
              </div>
            )}

            {/* 3. Mes ventes */}
            <div id="decouverte-tirelire-ventes" style={bloc}>
              <div style={etiquette}>🛍️ Mes ventes réelles</div>
              <div style={{ fontSize: ".64rem", color: C.gris, lineHeight: 1.5, marginBottom: ".5rem" }}>Note ici le prix de vente de ce que tu vends vraiment. Ta tirelire se remplit à chaque vente, et la vente s'ajoute aussi à tes « ventes perso » dans Objectifs : pas besoin de la noter deux fois.</div>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center", marginBottom: ".45rem" }}>
                <input type="text" inputMode="decimal" placeholder="Prix de vente" value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") ajouterVente(); }}
                  style={champ} />
                <span style={{ fontSize: "1.1rem", fontWeight: 700, color: C.brun }}>€</span>
              </div>
              <input type="text" placeholder="Qui / quoi ? (facultatif)" value={note} onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") ajouterVente(); }}
                style={{ ...champ, fontSize: ".82rem", fontWeight: 500, marginBottom: ".5rem" }} />
              <button onClick={ajouterVente} disabled={num(montant) <= 0}
                style={{ width: "100%", background: num(montant) > 0 ? `linear-gradient(135deg,${C.rose},${C.or})` : C.pale, color: "white", border: "none", borderRadius: 12, padding: ".7rem", fontSize: ".86rem", fontWeight: 700, fontFamily: "inherit", cursor: num(montant) > 0 ? "pointer" : "default" }}>
                {num(montant) > 0 ? `🪙 Mettre dans ma tirelire (+${fmtEuro(num(montant) * c.taux / 100)})` : "🪙 Mettre dans ma tirelire"}
              </button>

              {c.ventes.length > 0 && (
                <div style={{ marginTop: ".8rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".66rem", color: C.gris, marginBottom: ".3rem" }}>
                    <span>{c.ventes.length} vente{c.ventes.length > 1 ? "s" : ""}</span>
                    <span>Total vendu : <strong style={{ color: C.brun }}>{fmtEuro(c.totalVentes)}</strong></span>
                  </div>
                  {c.ventes.map((v) => (
                    <div key={v.id} style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".4rem 0", borderTop: `1px solid ${C.pale}` }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: ".78rem", fontWeight: 600, color: C.brun, wordBreak: "break-word" }}>{fmtEuro(num(v.montant))}{v.note ? <span style={{ fontWeight: 400, color: C.gris }}> · {v.note}</span> : null}</div>
                        <div style={{ fontSize: ".58rem", color: C.gris }}>{dateCourte(v.date)}</div>
                      </div>
                      <div style={{ fontSize: ".74rem", fontWeight: 700, color: C.vert, whiteSpace: "nowrap" }}>+{fmtEuro(num(v.montant) * c.taux / 100)}</div>
                      <button onClick={() => retirerVente(v.id)} aria-label="Retirer cette vente"
                        style={{ background: "none", border: "none", color: C.gris, fontSize: "1.05rem", cursor: "pointer", padding: "0 .2rem", lineHeight: 1 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Périodes précédentes */}
            {(t.historique || []).length > 0 && (
              <div style={bloc}>
                <div style={etiquette}>📅 Mes périodes précédentes</div>
                {[...t.historique].reverse().slice(0, 4).map((h, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: ".74rem", padding: ".3rem 0", borderTop: i ? `1px solid ${C.pale}` : "none" }}>
                    <span style={{ color: C.gris }}>Période {h.periode}</span>
                    <span style={{ fontWeight: 700, color: h.gainObj && h.gain >= h.gainObj ? C.vert : C.brun }}>
                      {fmtEuro(h.gain)}{h.gainObj ? <span style={{ fontWeight: 400, color: C.gris }}> / {fmtEuro(h.gainObj)}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: ".58rem", color: C.gris, textAlign: "center", lineHeight: 1.5 }}>
              Ta tirelire repart à zéro à chaque nouvelle période. Ton objectif et ton pourcentage sont conservés.
            </div>
        </FeuilleModale>
      )}

      {equipeOuverte && (
        <EquipePanel uid={uid} equipe={t.equipe || EQUIPE_VIDE} pvDefaut={pvObj > 0 ? pvObj : c.totalVentes}
          onChange={(eq) => maj((cur) => ({ ...cur, equipe: eq }))} onGain={onGainEquipe}
          onClose={() => setEquipeOuverte(false)} />
      )}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// 👥 RECRUTEMENT : une rangée de petits bonhommes qui s'allument à chaque nouvelle recrue.
// Source unique : l'objectif et le nombre de recrues de « Mes objectifs » (db-obj-perso),
// donc le classement, l'annuaire et l'onglet Objectifs restent parfaitement synchronisés.
// ─────────────────────────────────────────────────────────────────────────────

const entier = (v) => { const n = parseInt(String(v == null ? "" : v), 10); return isNaN(n) || n < 0 ? 0 : n; };
const OBJECTIFS_RECRUES = [1, 2, 3, 5, 8, 10];

const CSS_RECRUES = `
@keyframes tirAllume{0%{transform:scale(.6);opacity:.4}45%{transform:scale(1.35)}100%{transform:scale(1);opacity:1}}
`;

function Bonhomme({ allume, taille = 24, nouveau = false, sombre = true }) {
  const contour = allume ? "#B8861F" : sombre ? "rgba(255,255,255,.4)" : "#C9BBAE";
  const fondTete = allume ? "#F6D060" : sombre ? "rgba(255,255,255,.07)" : "#F1E9E2";
  const fondCorps = allume ? "#E8B83E" : sombre ? "rgba(255,255,255,.07)" : "#F1E9E2";
  return (
    <svg viewBox="0 0 24 32" width={taille} height={taille * 32 / 24} aria-hidden="true"
      style={{ display: "block", flexShrink: 0, filter: allume ? "drop-shadow(0 0 5px rgba(246,208,96,.95))" : "none", animation: nouveau ? "tirAllume .8s ease-out" : "none", transition: "filter .4s" }}>
      <path d="M3 31 v-6 a9 9 0 0 1 18 0 v6 z" fill={fondCorps} stroke={contour} strokeWidth="1.6" strokeLinejoin="round" />
      <circle cx="12" cy="8" r="6" fill={fondTete} stroke={contour} strokeWidth="1.6" />
      {allume && <circle cx="10" cy="6.4" r="1.6" fill="#fff" opacity=".7" />}
    </svg>
  );
}

function RecrutMoitie({ uid, userName }) {
  const [d, setD] = useState(null);              // { obj, count }
  const [tour, setTour] = useState(false);
  const [ouvert, setOuvert] = useState(false);
  const [objDraft, setObjDraft] = useState("");
  const [dernierAllume, setDernierAllume] = useState(-1);
  const [fete, setFete] = useState(0);
  const chaine = useRef(Promise.resolve());

  const charger = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      const o = snap.exists() && snap.data()["db-obj-perso"] ? JSON.parse(snap.data()["db-obj-perso"]) : {};
      setD({ obj: entier(o.recruesObj), count: entier(o.recruesReal) });
    } catch {
      setD((cur) => cur || { obj: 0, count: 0 });
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    charger();
    const t1 = setTimeout(charger, 2500);     // après la remise à zéro éventuelle d'une nouvelle période
    return () => clearTimeout(t1);
  }, [uid, charger]);
  useEffect(() => { if (ouvert) charger(); }, [ouvert, charger]);
  useEffect(() => {
    const ouvrir = () => setOuvert(true);
    window.addEventListener("ouvrir-recrues", ouvrir);
    return () => window.removeEventListener("ouvrir-recrues", ouvrir);
  }, []);

  // Écrit uniquement les 2 champs « recrues » dans db-obj-perso (lecture fraîche, pour ne rien écraser), puis prévient l'appli
  const ecrire = (champs) => {
    chaine.current = chaine.current.then(async () => {
      try {
        const ref = doc(db, "users", uid);
        const snap = await getDoc(ref);
        const o = snap.exists() && snap.data()["db-obj-perso"] ? JSON.parse(snap.data()["db-obj-perso"]) : {};
        const next = { ...o, ...champs };
        await setDoc(ref, { "db-obj-perso": JSON.stringify(next) }, { merge: true });
        try { await syncAnnuaire(uid, userName || uid, next); } catch { /* ignore */ }
        try { window.dispatchEvent(new CustomEvent("recrues-maj", { detail: { recruesObj: next.recruesObj, recruesReal: next.recruesReal } })); } catch { /* ignore */ }
      } catch (e) { console.warn("recrues:", e); }
    });
  };

  if (!uid) return null;
  if (!d) return <div style={{ flex: "1 1 0" }} />;

  const { obj, count } = d;
  const aObjectif = obj > 0;
  const atteint = aObjectif && count >= obj;

  const fixerObjectif = (v) => {
    const n = entier(v);
    setD((cur) => ({ ...cur, obj: n }));
    setObjDraft(n > 0 ? String(n) : "");
    ecrire({ recruesObj: n > 0 ? String(n) : "0" });
  };
  const ajouter = () => {
    const nc = count + 1;
    setD((cur) => ({ ...cur, count: nc }));
    setDernierAllume(nc - 1);
    ecrire({ recruesReal: String(nc) });
    if (aObjectif && count < obj && nc >= obj) {
      setFete((n) => n + 1);
      if (userName) { try { postToWallOfFame(uid, userName, "a atteint son objectif recrutement ! 👥", "🎉"); } catch { /* ignore */ } }
    }
  };
  const retirer = () => {
    if (count <= 0) return;
    setD((cur) => ({ ...cur, count: count - 1 }));
    setDernierAllume(-1);
    ecrire({ recruesReal: String(count - 1) });
  };

  const messageR = !aObjectif
    ? "Dis-moi combien de personnes tu veux accueillir, et chaque recrue allumera un bonhomme."
    : atteint ? "Objectif atteint, ton équipe grandit ! 🎉"
      : count === 0 ? "Ta première recrue va allumer le premier bonhomme 💡"
        : count >= obj - 1 ? "Plus qu'une pour compléter ton équipe ! ✨"
          : count >= obj / 2 ? "Plus de la moitié, continue ! 🔥" : "Ça s'allume, continue comme ça 💪";

  // Rangée compacte : jusqu'à 10 bonhommes (proportionnels si l'objectif est plus grand)
  const nbCompact = aObjectif ? Math.min(obj, 10) : Math.max(3, Math.min(count, 10));
  const alluCompact = aObjectif ? (obj > 10 ? Math.round(Math.min(1, count / obj) * 10) : Math.min(count, obj)) : Math.min(count, nbCompact);
  const tailleCompact = Math.max(11, Math.min(20, Math.floor(118 / nbCompact) - 3));
  const nbModal = aObjectif ? Math.min(Math.max(obj, count), 30) : Math.max(5, Math.min(count, 30));
  const tailleModal = nbModal <= 6 ? 46 : nbModal <= 12 ? 36 : 26;

  const champ = { width: "100%", boxSizing: "border-box", border: `1.5px solid ${C.pale}`, borderRadius: 10, padding: ".6rem .75rem", fontSize: "1rem", fontFamily: "inherit", color: C.brun, background: C.creme, outline: "none", fontWeight: 600 };
  const chip = (actif) => ({ padding: ".35rem .75rem", fontSize: ".72rem", fontWeight: 700, borderRadius: 20, cursor: "pointer", fontFamily: "inherit", border: `1.5px solid ${actif ? C.lilas || C.rose : C.pale}`, background: actif ? C.lilas || C.rose : C.blanc, color: actif ? "white" : C.gris });

  return (
    <>
      <style>{CSS_RECRUES}</style>

      {/* ── MOITIÉ « RECRUES » DU BANDEAU ── */}
      <div onClick={() => setOuvert(true)} role="button" aria-label="Ouvrir mes recrues"
        style={{ flex: "1 1 0", minWidth: 0, padding: ".4rem .7rem .4rem .6rem", borderLeft: `1px solid ${C.or}33`, display: "flex", flexDirection: "column", justifyContent: "center", cursor: "pointer" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: ".3rem" }}>
          <span style={{ fontSize: ".5rem", fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: C.or }}>Mes recrues</span>
          <span style={{ fontSize: ".72rem", fontWeight: 700, color: "white", whiteSpace: "nowrap" }}>
            {aObjectif ? <>{count}<span style={{ fontWeight: 400, opacity: .7, fontSize: ".6rem" }}> / {obj}</span></> : count > 0 ? count : ""}
          </span>
        </div>
        <div style={{ display: "flex", gap: 3, alignItems: "flex-end", marginTop: ".18rem" }}>
          {Array.from({ length: nbCompact }).map((_, i) => (
            <Bonhomme key={i} allume={i < alluCompact} taille={tailleCompact} nouveau={i === dernierAllume} />
          ))}
        </div>
        {!aObjectif && count === 0 && <div style={{ fontSize: ".54rem", color: C.pale, opacity: .85, marginTop: ".15rem" }}>Fixe ton objectif</div>}
      </div>

      {/* ── FENÊTRE DES RECRUES ── */}
      {ouvert && (
        <FeuilleModale onClose={() => setOuvert(false)}>
            {fete > 0 && <Confettis key={fete} />}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: ".6rem" }}>
              <div>
                <div style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem", color: C.brun }}>Mes <em style={{ color: C.lilas || C.rose }}>recrues</em></div>
                <div style={{ fontSize: ".62rem", color: C.gris }}>Chaque nouvelle recrue allume un bonhomme</div>
              </div>
              <button onClick={() => setOuvert(false)} aria-label="Fermer"
                style={{ background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: "50%", width: 34, height: 34, fontSize: "1rem", color: C.gris, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
            </div>

            <BoutonDecouverte onClick={() => setTour(true)} />
            {tour && <DecouverteTour outil="recrues" onClose={() => setTour(false)} />}

            <div id="decouverte-recrues-bonhommes" style={{ background: `linear-gradient(135deg,${C.brun},${C.brun2 || C.brun})`, borderRadius: 16, padding: "1.1rem .8rem", textAlign: "center", marginBottom: ".8rem" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: tailleModal > 30 ? 10 : 6, justifyContent: "center", alignItems: "flex-end", minHeight: tailleModal * 1.4 }}>
                {Array.from({ length: nbModal }).map((_, i) => (
                  <Bonhomme key={i} allume={i < count} taille={tailleModal} nouveau={i === dernierAllume} />
                ))}
              </div>
              <div style={{ fontFamily: "Georgia,serif", fontSize: "1.9rem", fontWeight: 600, color: "white", marginTop: ".6rem", lineHeight: 1.1 }}>
                {count}{aObjectif && <span style={{ fontSize: "1rem", fontWeight: 400, opacity: .7 }}> / {obj}</span>}
              </div>
              <div style={{ fontSize: ".72rem", color: C.pale, opacity: .9 }}>recrue{count > 1 ? "s" : ""} cette période{count > obj && aObjectif ? ` · +${count - obj} au-delà de l'objectif` : ""}</div>
              <div style={{ fontSize: ".76rem", color: "#F6D060", marginTop: ".45rem", fontStyle: "italic", lineHeight: 1.45 }}>{messageR}</div>
            </div>

            <button id="decouverte-recrues-bouton" onClick={ajouter}
              style={{ width: "100%", background: `linear-gradient(135deg,${C.lilas || C.rose},${C.rose})`, color: "white", border: "none", borderRadius: 14, padding: ".85rem", fontSize: ".92rem", fontWeight: 700, fontFamily: "inherit", cursor: "pointer", marginBottom: ".4rem" }}>
              ✨ Une nouvelle recrue !
            </button>
            {count > 0 && (
              <button onClick={retirer}
                style={{ background: "none", border: "none", color: C.gris, fontSize: ".66rem", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", display: "block", margin: "0 auto .8rem" }}>
                J'ai ajouté une recrue par erreur (retirer)
              </button>
            )}

            <div id="decouverte-recrues-objectif" style={{ background: C.blanc, border: `1px solid ${C.pale}`, borderRadius: 14, padding: ".9rem", marginBottom: ".7rem" }}>
              <div style={{ fontSize: ".62rem", fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: C.lilas || C.rose, marginBottom: ".4rem" }}>🎯 Combien de recrues je vise</div>
              <div style={{ display: "flex", gap: ".5rem", alignItems: "center", marginBottom: ".5rem" }}>
                <input type="text" inputMode="numeric" placeholder="Tape ton objectif (ex : 3)" value={objDraft || (aObjectif ? String(obj) : "")}
                  onChange={(e) => setObjDraft(e.target.value.replace(/\D/g, ""))}
                  onBlur={() => { if (objDraft !== "") fixerObjectif(objDraft); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { fixerObjectif(objDraft); e.currentTarget.blur(); } }}
                  style={champ} />
              </div>
              <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                {OBJECTIFS_RECRUES.map((v) => <button key={v} onClick={() => fixerObjectif(v)} style={chip(obj === v)}>{v}</button>)}
              </div>
              <div style={{ fontSize: ".62rem", color: C.gris, lineHeight: 1.5, marginTop: ".5rem" }}>
                Une recrue = une personne qui rejoint ton équipe. Ce chiffre est le même que dans Tableau de bord → Objectifs, il reste donc toujours à jour partout.
              </div>
            </div>

            <div style={{ fontSize: ".58rem", color: C.gris, textAlign: "center", lineHeight: 1.5 }}>
              Le compteur repart à zéro à chaque nouvelle période.
            </div>
        </FeuilleModale>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Bandeau toujours visible en haut de l'appli : la tirelire à gauche, les recrues à droite
// ─────────────────────────────────────────────────────────────────────────────
export function TirelireBandeau({ uid, userName }) {
  if (!uid) return null;
  return (
    <div style={{ display: "flex", background: `linear-gradient(135deg,${C.brun},${C.brun2 || C.brun})`, borderBottom: `1px solid ${C.or}55` }}>
      <TirelireMoitie uid={uid} userName={userName} />
      <RecrutMoitie uid={uid} userName={userName} />
    </div>
  );
}
